/* locale.js
   ----------------------------------------------------------------------
   Single source of truth for the three site languages.

   URL shape (chosen for SEO: every language is its own indexable URL):
     English  →  /            /programs            /trips/dubai-winter
     Arabic   →  /ar          /ar/programs         /ar/trips/dubai-winter
     Russian  →  /ru          /ru/programs         /ru/trips/dubai-winter

   English is the default and carries NO prefix, so every existing
   inbound link, ad URL, and Google result keeps working untouched.

   The language list and the pure URL helpers live in src/seo/site.mjs
   so the build scripts (sitemap, pre-render) can import them with plain
   Node. They are re-exported here so every existing import keeps
   working unchanged.

   Copy lives in the page files as a locale triple:
       t({ en: "Book Now", ar: "احجز الآن", ru: "Записаться" })
   `resolve` below is what turns a triple into a string. It walks
   arrays and nested objects too, so a whole data record can be
   resolved in one call (see useL in LocaleContext.jsx). */

import { LOCALES, DEFAULT_LOCALE, localeFromPath, stripLocale, localizePath } from "../seo/site.mjs";

export { LOCALES, DEFAULT_LOCALE, localeFromPath, stripLocale, localizePath };

export const LOCALE_META = {
  en: { code: "en", label: "EN", name: "English",  htmlLang: "en", dir: "ltr", ogLocale: "en_AE", hreflang: "en" },
  ar: { code: "ar", label: "AR", name: "العربية",  htmlLang: "ar", dir: "rtl", ogLocale: "ar_AE", hreflang: "ar" },
  ru: { code: "ru", label: "RU", name: "Русский",  htmlLang: "ru", dir: "ltr", ogLocale: "ru_RU", hreflang: "ru" },
};

/* True for a copy triple: { en, ar, ru } (ar / ru may be missing while
   a translation is still pending — `en` is the fallback). */
function isCopyTriple(value) {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  if (value.$$typeof) return false;                        /* React element */
  const keys = Object.keys(value);
  if (keys.length === 0 || !("en" in value)) return false;
  return keys.every((k) => k === "en" || k === "ar" || k === "ru");
}

/* Turn triples into strings for one locale, at any depth.
   Strings, numbers, null, JSX and class instances pass through. */
export function resolve(value, locale = DEFAULT_LOCALE) {
  if (isCopyTriple(value)) {
    const picked = value[locale];
    return picked == null || picked === "" ? value.en : picked;
  }
  if (Array.isArray(value)) return value.map((v) => resolve(v, locale));
  if (value && typeof value === "object" && !value.$$typeof && value.constructor === Object) {
    const out = {};
    for (const [k, v] of Object.entries(value)) out[k] = resolve(v, locale);
    return out;
  }
  return value;
}

/* English values, whatever the visitor's language. Used for anything
   that must stay stable across languages: Firestore payloads (the
   security rules pin exact field values), analytics labels, and the
   Stripe / payment metadata. */
export function resolveEn(value) {
  return resolve(value, DEFAULT_LOCALE);
}
